<script setup>
import { ref, computed } from 'vue';
import { getUuid, hexToRgb } from '@/utils/api';
import { useCommonData } from '@/hooks';

const { getUpperThemeColor } = useCommonData();

const props = defineProps({
	esgData: {
		type: Array,
		default: () => []
	},
	ratingConfig: {
		type: Array,
		default: () => []
	},
	loading: {
		type: Boolean,
		default: false
	}
});

const activeId = ref('');

// 保持与历史组件相同的数据处理逻辑（左右分栏）
const esgList = computed(() => {
	if (!props.esgData?.length) return [[], []];

	const esgDataMap = props.esgData.reduce((obj, ele) => {
		ele.date = ele.pubDate.replace(/-/g, '.');
		obj[ele.orgName] = ele;
		return obj;
	}, {});

	const left = [],
		right = [];
	let i = 0;
	for (let { name } of props.ratingConfig) {
		const item = esgDataMap[name];
		if (!item) {
			continue;
		}
		item.uuid = getUuid();
		if (item.esgRateRatioVOList) {
			item.esgRateRatioVOList = item.esgRateRatioVOList.map(rating => {
				rating.rateNumRatio = parseFloat(rating.rateNumRatio).toFixed(2);
				return rating;
			});
			item.reverseEsgRateRatioVOList = [].concat(item.esgRateRatioVOList).reverse();
		}
		if (i % 2 === 0) left.push(item);
		else right.push(item);
		i++;
	}
	return [left, right];
});

function getPieBg(value, active) {
	const _value = Math.max(parseFloat(value), 1.5) + '%';
	const emphasisColor = active ? '#fff' : getUpperThemeColor.value;
	const emphasisColorRGB = hexToRgb(emphasisColor);
	const normalColor = `rgba(${emphasisColorRGB.r}, ${emphasisColorRGB.g}, ${emphasisColorRGB.b}, ${
		active ? 0.2 : 0.1
	})`;
	return {
		background: `conic-gradient(
            ${emphasisColor} 0% ${_value},
            ${normalColor} ${_value} 100%
        )`
	};
}

const emits = defineEmits(['handleRating']);
const handleRating = item => {
	emits('handleRating', item);
};
</script>

<template>
	<div class="esg-rating-wrapper" v-loading="loading">
		<p class="title">{{ $t('enterprise.esg') }}</p>
		<div v-if="esgList[0].length" class="rating-wrapper">
			<div class="rating-left">
				<div
					class="rating-item"
					v-for="(item, index) in esgList[0]"
					:key="index"
					@mouseenter="activeId = item.uuid"
					@mouseleave="activeId = ''"
					@click="handleRating(item)"
				>
					<div class="rating-header">
						<div class="rating-value">
							{{ item.rate || item.score }}
						</div>
						<div class="indicator-time">
							<p class="indicator">{{ item.orgName }}</p>
							<p class="time">{{ item.date }}</p>
						</div>
					</div>
					<template v-if="item.esgRateRatioVOList">
						<div class="rating-list">
							<p
								:class="{ 'active-rating': rating.rate === item.rate }"
								v-for="rating in item.reverseEsgRateRatioVOList"
								:key="rating.rate"
							>
								{{ rating.rate }}
							</p>
						</div>
						<div v-show="activeId === item.uuid" class="rating-distribution">
							<div
								class="distribution-item"
								:class="{ 'active-distribution': rating.rate === item.rate }"
								v-for="rating in item.esgRateRatioVOList"
								:key="rating.rate"
							>
								<div class="distribution-left">{{ rating.rate }}</div>
								<div class="distribution-right">
									<div class="distribution-ratio">{{ rating.rateNumRatio }}%</div>
									<div
										class="distribution-pie"
										:style="getPieBg(`${rating.rateNumRatio}`, rating.rate === item.rate)"
									></div>
								</div>
							</div>
						</div>
					</template>
				</div>
			</div>
			<div class="rating-right">
				<div
					class="rating-item"
					v-for="(item, index) in esgList[1]"
					:key="index"
					@mouseenter="activeId = item.uuid"
					@mouseleave="activeId = ''"
					@click="handleRating(item)"
				>
					<div class="rating-header">
						<div class="rating-value">{{ item.rate || item.score }}</div>
						<div class="indicator-time">
							<p class="indicator">{{ item.orgName }}</p>
							<p class="time">{{ item.date }}</p>
						</div>
					</div>
					<template v-if="item.esgRateRatioVOList">
						<div class="rating-list">
							<p
								:class="{ 'active-rating': rating.rate === item.rate }"
								v-for="rating in item.reverseEsgRateRatioVOList"
								:key="rating.rate"
							>
								{{ rating.rate }}
							</p>
						</div>
						<div v-show="activeId === item.uuid" class="rating-distribution">
							<div
								class="distribution-item"
								:class="{ 'active-distribution': rating.rate === item.rate }"
								v-for="rating in item.esgRateRatioVOList"
								:key="rating.rate"
							>
								<div class="distribution-left">{{ rating.rate }}</div>
								<div class="distribution-right">
									<div class="distribution-ratio">{{ rating.rateNumRatio }}%</div>
									<div
										class="distribution-pie"
										:style="getPieBg(`${rating.rateNumRatio}%`, rating.rate === item.rate)"
									></div>
								</div>
							</div>
						</div>
					</template>
				</div>
			</div>
		</div>
		<Empty v-else-if="!loading && !esgList[0].length"></Empty>
	</div>
</template>

<style lang="less" scoped>
.esg-rating-wrapper {
	.title {
		color: var(--emphasis-color);
		font-size: 16px;
		font-weight: 600;
		line-height: 24px;
	}

	.rating-wrapper {
		margin-top: 20px;
		display: flex;

		.rating-left,
		.rating-right {
			width: calc(50% - 8px);
		}
		.rating-right {
			margin-left: 16px;
		}

		.rating-item {
			padding: 12px;
			background: var(--theme-color-gradient-010);
			cursor: pointer;

			.rating-header {
				display: flex;
				align-items: center;
				justify-content: space-between;
			}

			.rating-value {
				height: 40px;
				padding: 0 8px;
				border-radius: 2px;
				background: var(--theme-color);

				color: #fff;
				font-family: 'DIN Alternate';
				font-size: 28px;
				font-weight: 700;
				line-height: 40px;
			}
			.indicator {
				color: var(--emphasis-color);
				text-align: right;
				font-size: 20px;
				font-weight: 600;
				line-height: 24px; /* 120% */
			}
			.time {
				margin-top: 8px;

				color: var(--secondary-info-color);
				text-align: right;
				font-size: 12px;
				font-weight: 400;
				line-height: 16px;
			}

			.rating-list {
				margin-top: 16px;
				height: 24px;
				padding: 0 12px;
				border: 1px solid var(--theme-color);

				display: flex;
				justify-content: space-between;

				color: var(--theme-color);
				font-size: 12px;
				font-weight: 400;
				line-height: 24px; /* 133.333% */

				.active-rating {
					padding: 0 6px;
					background: var(--theme-color);
					color: #fff;
				}
			}

			.rating-distribution {
				margin-top: 16px;

				display: grid;
				grid-template-columns: repeat(3, 1fr);
				grid-gap: 12px;

				.distribution-item {
					height: 24px;
					padding: 0 8px;
					border-radius: 2px;
					background: rgba(0, 0, 0, 0.03);
					display: flex;
					align-items: center;
					justify-content: space-between;

					color: var(--secondary-color);
					font-size: 12px;
					font-weight: 400;

					&.active-distribution {
						background: var(--theme-color);
					}

					.distribution-right {
						display: flex;
						align-items: center;

						font-family: 'DIN Alternate';
						font-weight: 700;
					}

					.distribution-pie {
						margin-left: 12px;

						width: 16px;
						height: 16px;
						border-radius: 50%;
					}

					&.active-distribution {
						background: var(--theme-color);
						color: #fff;
					}
				}
			}
		}
		.rating-item + .rating-item {
			margin-top: 16px;
		}
	}
}
</style>
